import { ColumnDef } from '@tanstack/react-table'
import { ArrowUpDown, MoreHorizontal, Eye, FileText, User } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { Invoice } from '@/lib/api-client'
import { formatCurrency } from '@/lib/utils'

// Branch colors matching dashboard
const BRANCH_COLORS: Record<string, string> = {
  'Shah Alam': '#f59e0b',
  'Putrajaya': '#4a8fe8',
  'Bangi': '#10b981',
}

// Status colors
const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  'Paid': { bg: '#10b981', text: 'white' },
  'Pending': { bg: '#f59e0b', text: 'white' },
  'Void': { bg: '#ef4444', text: 'white' },
}

// Patient type colors
const PATIENT_TYPE_COLORS: Record<string, { bg: string; text: string }> = {
  'New': { bg: '#3b82f6', text: 'white' },
  'Existing': { bg: '#8b5cf6', text: 'white' },
}

interface InvoiceColumnsProps {
  onView: (invoice: Invoice) => void
  onViewPatient?: (patientId: number) => void
}

export function getInvoiceColumns({ onView, onViewPatient }: InvoiceColumnsProps): ColumnDef<Invoice>[] {
  return [
    // Selection checkbox
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
          onClick={(e) => e.stopPropagation()}
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },

    // Invoice No
    {
      accessorKey: 'invoiceNo',
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="font-semibold px-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Invoice No
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <span className="font-medium">{row.getValue('invoiceNo')}</span>
      ),
    },

    // Invoice Date
    {
      accessorKey: 'invoiceDate',
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="font-semibold px-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Date
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const date = row.getValue('invoiceDate') as string | null
        if (!date) return <span className="text-muted-foreground">-</span>

        const formatted = new Date(date).toLocaleDateString('en-MY', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })
        return <div>{formatted}</div>
      },
    },

    // Patient Name
    {
      accessorKey: 'patientName',
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="font-semibold px-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Patient
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const name = row.getValue('patientName') as string | null
        const pno = row.original.patientPno
        return (
          <div>
            <div className="font-medium">{name || 'Unknown'}</div>
            {pno && <div className="text-xs text-muted-foreground">{pno}</div>}
          </div>
        )
      },
    },

    // Grand Total
    {
      accessorKey: 'grandTotal',
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="font-semibold px-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Amount
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const amount = row.getValue('grandTotal') as string | null
        const value = parseFloat(amount || '0')
        return <div className="text-right font-medium">{formatCurrency(value)}</div>
      },
    },

    // Status
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const status = row.getValue('status') as string | null
        if (!status) return <span className="text-muted-foreground">-</span>

        const colors = STATUS_COLORS[status] || { bg: '#6b7280', text: 'white' }
        return (
          <Badge style={{ background: colors.bg, color: colors.text }}>
            {status}
          </Badge>
        )
      },
    },

    // Patient Type
    {
      accessorKey: 'patientType',
      header: 'Type',
      cell: ({ row }) => {
        const type = row.getValue('patientType') as string | null
        if (!type) return <span className="text-muted-foreground">-</span>

        const colors = PATIENT_TYPE_COLORS[type] || { bg: '#6b7280', text: 'white' }
        return (
          <Badge style={{ background: colors.bg, color: colors.text }}>
            {type}
          </Badge>
        )
      },
    },

    // Branch with colored badge
    {
      accessorKey: 'branchName',
      header: 'Branch',
      cell: ({ row }) => {
        const branch = row.getValue('branchName') as string | null
        const color = branch ? BRANCH_COLORS[branch] || '#6b7280' : '#6b7280'
        return branch ? (
          <Badge style={{ background: color }} className="text-white">
            {branch}
          </Badge>
        ) : null
      },
    },

    // Payment Method
    {
      accessorKey: 'paymentMethod',
      header: 'Payment',
      cell: ({ row }) => {
        const method = row.getValue('paymentMethod') as string | null
        return method ? (
          <span className="text-sm">{method}</span>
        ) : (
          <span className="text-muted-foreground">-</span>
        )
      },
    },

    // Actions
    {
      id: 'actions',
      enableHiding: false,
      cell: ({ row }) => {
        const invoice = row.original

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="h-9 w-9 p-0"
                onClick={(e) => e.stopPropagation()}
              >
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel className="text-[13px]">Actions</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => onView(invoice)} className="text-[13px]">
                <Eye className="mr-2 h-4 w-4" />
                View Details
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onView(invoice)} className="text-[13px]">
                <FileText className="mr-2 h-4 w-4" />
                View Items
              </DropdownMenuItem>
              {invoice.patientId && onViewPatient && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => onViewPatient(invoice.patientId!)}
                    className="text-[13px]"
                  >
                    <User className="mr-2 h-4 w-4" />
                    View Patient
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
    },
  ]
}
